/**
 * **一排 / 一扇牌的节点** —— 每张都是一个 `CardView`，动效交给一个 `CardRig`。
 *
 * 这是 `HandBacks` 的替代品：抽牌堆、旁观扇、取牌扇都改成它，于是它们和手牌
 * **同一套节点与动效**（idle 摇摆 / hover 抬起 / 脉动 / 出血裁切），不再各写一套。
 *
 * 牌背用一个**按尺寸 memoize** 的纹理（`renderer.generateTexture(paintClippedFace(backFace()))`，
 * `pad = 0`）；牌面走共享的 `CardFaceCache`。
 */
import { Container, Rectangle, type Renderer, type Text, type Texture } from 'pixi.js';
import { CardRig } from '../hand/cardRig';
import { richLabel } from '../ui/richText';
import { INK } from '../ui/style';
import { CardView } from '../card/cardView';
import { DEFAULT_POSE, type CardPose } from '../card/pose';
import { faceSize, type FaceSize } from '../card/metrics';
import { backFaceTexture } from '../card/backFaceTexture';
import type { CardDef } from '../../core/card';
import type { CardFaceCache } from '../card/faceCache';
import type { CardTitle } from '../../core/ids';
import { layoutArc } from './layoutArc';
import { canHoverNow } from '../input/current';

export type StripItem = {
  readonly kind: 'card';
  readonly title: CardTitle | null;
  /**
   * **这一张前面那段间距的倍率**（默认 1）。
   *
   * 抽牌堆用它把"连续两张未知牌之间"的间距压到 0.2 倍 —— 不是把它们
   * 分成一个"未知牌叠"，而是逐张摆、逐张调间距。
   */
  readonly gapScale?: number;
  /** 盖在这张牌上方的数字（连续 >4 张未知牌给中间那张带一个） */
  readonly label?: string | null;
};

export interface CardStripOptions {
  readonly renderer: Renderer;
  readonly height: number;
  readonly faces?: CardFaceCache;
  readonly defOf?: (title: CardTitle) => CardDef | undefined;
  readonly maxSpread?: number;
  readonly maxWidth?: number;
  readonly maxTilt?: number;
  readonly curvature?: number;
  readonly randomRotation?: boolean;
  readonly yOffset?: number;
  readonly interactive?: boolean;
  readonly pickable?: boolean;
  readonly clip?: boolean;
  readonly onHover?: (slot: number | null) => void;
  readonly onPick?: (slot: number) => void;
}

interface Slot {
  readonly view: CardView;
  readonly title: CardTitle | null;
  /** 这一段间距 / 上方数字（没有就是 null） */
  readonly gapScale: number;
  readonly label: Text | null;
  tx: number;
  ty: number;
  trot: number;
  hover: number;
}

export class CardStrip {
  readonly view: Container;
  readonly size: FaceSize;

  readonly #rig: CardRig;
  readonly #options: CardStripOptions;
  #slots: Slot[] = [];
  #place: number[] = [];
  #maxSpread: number;
  #maxTilt: number;
  #randomRotation: boolean;
  #yOffset: number;
  #curvature: number;
  #highlight: number | null = null;
  /** 远端拖拽：被拖那张的**绝对位置**（相对扇心，单位 = 牌高） */
  #remoteDrag: { slot: number; x: number; y: number } | null = null;
  #built = false;
  #onHover: ((slot: number | null) => void) | undefined;
  #onPick: ((slot: number) => void) | undefined;
  /** 本地拖拽（塞回异常）：被拖的那一位跟指针走，其余不动 */
  #dragPlace: number | null = null;
  #dragX = 0;
  /**
   * 拖拽时其余牌要不要"让位"（开一个格子的空档）。
   *
   * ⚠️ 塞回异常时关掉：抽牌堆整组是靠 `DrawPile` 平滑平移的，再叠一层
   *    让位位移会变成两套运动互相追，看起来就是抽搐（用户报的）。
   */
  #gapOffsetOn = true;
  /** 上一次画的是哪些项。没变就不重建（牌扇每帧 `setPile`，不重建才保得住拖拽态） */
  #signature: string | null = null;
  /** 上一次布局用的固定间距（拖拽让位时按它挪一格） */
  #spread = 0;

  constructor(options: CardStripOptions) {
    this.#options = options;
    this.size = faceSize(options.height);
    this.#maxSpread = options.maxSpread ?? 16;
    this.#maxTilt = options.maxTilt ?? 9;
    this.#randomRotation = options.randomRotation ?? false;
    this.#yOffset = options.yOffset ?? 0;
    this.#curvature = options.curvature ?? 0.16;
    this.view = new Container();
    this.view.label = 'card-strip';
    this.view.eventMode = options.interactive === false ? 'none' : 'passive';
    this.#onHover = options.onHover;
    this.#onPick = options.onPick;
    this.#rig = new CardRig({ view: this.view });
  }

  /**
   * 换 hover / 点选回调（运行期切换"旁观 vs 取牌"用）。
   *
   * 槽位在 `setItems` 时就绑好了监听，所以这里只换**收到之后发给谁**，
   * 不用重建节点。
   */
  setHandlers(handlers: {
    onHover?: ((slot: number | null) => void) | undefined;
    onPick?: ((slot: number) => void) | undefined;
  }): void {
    if ('onHover' in handlers) this.#onHover = handlers.onHover;
    if ('onPick' in handlers) this.#onPick = handlers.onPick;
  }

  get count(): number {
    return this.#slots.length;
  }

  /** 内容宽度（一张牌的宽度 + 各段间距，按 `gapScale` 实算） */
  get width(): number {
    const n = this.#slots.length;
    if (n <= 1) return this.size.width;
    const spread = Math.min(this.#maxSpread, (this.#options.maxWidth ?? 260) / Math.max(1, n));
    let total = this.size.width;
    for (let place = 1; place < n; place++) {
      const slot = this.#slots[this.#place[place] ?? -1];
      total += (slot?.gapScale ?? 1) * spread;
    }
    return total;
  }

  /** 现在的 hover 位次（读写） */
  get highlight(): number | null {
    return this.#highlight;
  }
  set highlight(value: number | null) {
    this.#highlight = value;
  }

  setMaxSpread(value: number): void {
    if (value === this.#maxSpread) return;
    this.#maxSpread = value;
    this.#relayout();
  }

  /** 运行期切换观感（抽牌堆收起/展开用）：倾角 / 随机角 / 纵向错位 */
  setFanLook(options: { maxTilt?: number; randomRotation?: boolean; yOffset?: number }): void {
    if (options.maxTilt !== undefined) this.#maxTilt = options.maxTilt;
    if (options.randomRotation !== undefined) this.#randomRotation = options.randomRotation;
    if (options.yOffset !== undefined) this.#yOffset = options.yOffset;
    this.#relayout();
  }

  /** 现在画了几张 */
  get rendered(): number {
    return this.#slots.length;
  }

  /** 全是牌背的一叠 */
  setCount(count: number): void {
    const n = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
    this.setItems(Array.from({ length: n }, () => ({ kind: 'card', title: null })));
  }

  setItems(items: readonly StripItem[]): void {
    const signature = items
      .map((item) => (item.title ?? '-') + '/' + String(item.gapScale ?? 1) + '/' + (item.label ?? ''))
      .join('|');
    if (signature === this.#signature) return;
    this.#signature = signature;
    // 整扇重建（项很少，且 `CardView` 走牌面缓存）
    this.#dragPlace = null;
    for (const slot of this.#slots) slot.label?.destroy();
    this.#rig.clear();
    this.#slots = [];
    this.#place = items.map((_item, i) => i);
    this.#built = false;
    for (const item of items) {
      const texture = this.#textureFor(item.title);
      // ★ 牌面**保留出血**（和手牌同一套），牌背保持"裁到框内"这一版
      const clip = item.title === null ? true : (this.#options.clip ?? false);
      const view = new CardView({ texture, size: this.size, clip });
      const text = item.label == null || item.label === ''
        ? null
        : richLabel({ text: item.label, scale: 1, size: 20, fill: INK.text, role: 'cjk', align: 'center', bold: true });
      if (text !== null) this.view.addChild(text);
      const index = this.#slots.length;
      const slot: Slot = {
        view,
        title: item.title,
        gapScale: item.gapScale ?? 1,
        label: text,
        tx: 0,
        ty: 0,
        trot: 0,
        hover: 0,
      };
      this.#slots.push(slot);
      this.#rig.add(view);
      // 牌背也要能点（取牌扇就是一扇牌背）
      if (this.#options.pickable ?? false) this.#bindSlot(view, index);
    }
    this.#built = true;
    this.#relayout();
  }

  #textureFor(title: CardTitle | null): Texture {
    const { faces, defOf } = this.#options;
    if (title !== null && faces !== undefined && defOf !== undefined) {
      const def = defOf(title);
      if (def !== undefined) return faces.get(def, this.size);
    }
    return backFaceTexture(this.#options.renderer, this.size);
  }

  #bindSlot(view: CardView, index: number): void {
    view.eventMode = 'static';
    view.cursor = 'pointer';
    // ⚠️ `CardView` 的原点在**牌心**（网格绕中心铺），所以命中框要居中，
    //    不能从 (0,0) 起 —— 否则 hover / 点选会整体偏半个牌。
    view.hitArea = new Rectangle(-this.size.width / 2, -this.size.height / 2, this.size.width, this.size.height);
    const slotOf = (): number => this.#place.indexOf(index);
    // 装饰性 hover 只对能 hover 的指针生效（触控点完指针位置会滞留）
    view.on('pointerover', () => {
      if (!canHoverNow()) return;
      this.#highlight = slotOf();
      this.#onHover?.(this.#highlight);
    });
    view.on('pointerout', () => {
      if (this.#highlight === null) return;
      this.#highlight = null;
      this.#onHover?.(null);
    });
    view.on('pointertap', () => this.#onPick?.(slotOf()));
  }

  /**
   * 远端拖拽重放。`x` / `y` 是**绝对位置**（相对扇心，单位 = 牌高），
   * 不是相对槽位的位移 —— 换位时相对值会跳，绝对位置不会。
   */
  setRemoteDrag(slot: number | null, x = 0, y = 0): void {
    this.#remoteDrag = slot === null ? null : { slot, x, y };
    this.#relayout();
  }

  // ---- 本地拖拽（塞回异常用；走同一套 override 手感）----

  /** 正在拖着哪一位（false = 没拖） */
  get dragging(): boolean {
    return this.#dragPlace !== null;
  }

  /** 被拖那张现在的本地 x（没在拖 = null） */
  get dragX(): number | null {
    return this.#dragPlace === null ? null : this.#dragX;
  }

  /** 拖拽时其余牌要不要让位（塞回异常关掉，见 `#gapOffsetOn`） */
  setGapOffset(on: boolean): void {
    if (this.#gapOffsetOn === on) return;
    this.#gapOffsetOn = on;
    this.#relayout();
  }

  /**
   * 强制进入拖拽态（塞回异常：牌没有被按着，也要跟指针走）。
   *
   * 走的是 CardStep.override —— 和手牌拖动是同一个机制：位置脱离槽位、
   * 直接落在指针上，而倾斜 / 缩放 / 平滑照旧由 CardRig 负责。
   */
  beginDrag(place: number): void {
    if (place < 0 || place >= this.#slots.length) return;
    const slot = this.#slots[this.#place[place] ?? -1];
    this.#dragPlace = place;
    this.#dragX = slot?.tx ?? 0;
    this.#highlight = place;
  }

  /** 指针现在在哪（整扇本地坐标） */
  dragTo(x: number): void {
    if (this.#dragPlace === null) return;
    this.#dragX = x;
  }

  /** 松手 / 取消：回到槽位 */
  endDrag(): void {
    if (this.#dragPlace === null) return;
    this.#dragPlace = null;
    this.#highlight = null;
    this.#relayout();
  }

  /**
   * 落点 x 对应的插入位：有几个槽位中心在它左边。
   *
   * 位次 0 在最左；返回 0..count（count = 排最右那张之后）。调用方按语义
   * 决定要不要减掉正在拖的那张。
   */
  indexAt(x: number): number {
    const n = this.#slots.length;
    let index = 0;
    for (let place = 0; place < n; place++) {
      const slot = this.#slots[this.#place[place] ?? -1];
      if (slot === undefined) continue;
      if (slot.tx <= x) index = place + 1;
      else break;
    }
    return index;
  }

  reorder(from: number, to: number): void {
    if (from === to || from < 0 || to < 0 || from >= this.#place.length || to >= this.#place.length) return;
    const [moved] = this.#place.splice(from, 1);
    if (moved === undefined) return;
    this.#place.splice(to, 0, moved);
    this.#relayout();
  }

  /** 第 slot 位的牌名（只对牌面的牌有意义） */
  titleAt(slot: number): CardTitle | null {
    const index = this.#place[slot];
    return index === undefined ? null : (this.#slots[index]?.title ?? null);
  }

  /** 第 slot 位的视图（tooltip 用它的包围盒） */
  slotView(slot: number): Container | null {
    const index = this.#place[slot];
    return index === undefined ? null : (this.#slots[index]?.view ?? null);
  }

  /**
   * **第 `place` 位的布局中心 x**（不含拖拽让位 / override，就是 `#relayout`
   * 算出来的 `tx`）。
   *
   * 塞回异常的"屏幕横轴 → 插入位"映射要靠它把落点那一段平移进视口。
   */
  slotCenterX(place: number): number | null {
    const index = this.#place[place];
    const slot = index === undefined ? undefined : this.#slots[index];
    return slot === undefined ? null : slot.tx;
  }

  /** 位次 → 位置（相对整扇原点；沿用 `HandBacks` 的"左上角"约定） */
  slotPosition(slot: number): { x: number; y: number } | null {
    const index = this.#place[slot];
    if (index === undefined) return null;
    const view = this.#slots[index]?.view;
    if (view === undefined) return null;
    return { x: view.x - this.size.width / 2, y: view.y - this.size.height / 2 };
  }

  #relayout(): void {
    const n = this.#slots.length;
    if (n === 0) return;
    const spread = Math.min(this.#maxSpread, (this.#options.maxWidth ?? Infinity) / Math.max(1, n));
    this.#spread = spread;
    const places = layoutArc({
      count: n,
      spread,
      curvature: this.#curvature,
      maxTiltDeg: this.#maxTilt,
      randomRotation: this.#randomRotation,
      yOffset: this.#yOffset,
      cardHeight: this.size.height,
      order: this.#place,
    });
    // 逐张累加"这段间距相对正常间距的额外位移"：连续未知牌之间 0.2× 就是这里
    let extra = 0;
    for (let place = 0; place < n; place++) {
      const index = this.#place[place];
      const slot = index === undefined ? undefined : this.#slots[index];
      const at = places[place];
      if (slot === undefined || at === undefined) continue;
      if (place > 0) extra += (slot.gapScale - 1) * spread;
      slot.tx = this.size.width / 2 + at.x + extra;
      slot.ty = this.size.height / 2 + at.y;
      slot.trot = at.rotation;
      const drag = this.#remoteDrag;
      if (drag !== null && drag.slot === place) {
        // 绝对定位：直接放在"扇心 + 相对偏移"处，不叠在槽位上
        slot.tx = this.size.width / 2 + drag.x * this.size.height;
        slot.ty = this.size.height / 2 + drag.y * this.size.height;
      }
    }
  }

  /**
   * **拖拽让位**：正在拖的那张左右，其余牌空出一格。
   *
   * 位次 `place` 该额外偏移多少 x —— `#dragX` 落在哪两格之间，就把那一段
   * 整段挪开（`#spread` 一格）。被拖那张自己不偏（它走 `override`）。
   */
  #gapOffset(place: number): number {
    const p = this.#dragPlace;
    if (p === null) return 0;
    const n = this.#slots.length;
    // 目标插入位：不计正在拖的那张，看指针左边有几格
    let target = 0;
    for (let i = 0; i < n; i++) {
      if (i === p) continue;
      const slot = this.#slots[this.#place[i] ?? -1];
      if (slot !== undefined && slot.tx <= this.#dragX) target += 1;
    }
    if (place === p) return 0;
    // 往右拖：中间那些格整体左移；往左拖：整体右移
    if (p < target && place > p && place <= target) return -this.#spread;
    if (p > target && place >= target && place < p) return this.#spread;
    return 0;
  }

  /** 每帧推（宿主给毫秒） */
  step(deltaMS: number): void {
    if (!this.#built || this.#slots.length === 0) return;
    const dt = Math.min(deltaMS / 1000, 0.05);
    const gap = this.#dragPlace === null || !this.#gapOffsetOn ? 0 : 1;
    const specs = this.#slots.map((slot, index) => {
      const place = this.#place.indexOf(index);
      const hovered = this.#highlight === place;
      const dragging = this.#dragPlace === place;
      const posture: CardPose = {
        ...DEFAULT_POSE,
        x: slot.tx + (gap === 0 ? 0 : this.#gapOffset(place)),
        y: slot.ty,
        roll: slot.trot,
      };
      return {
        posture,
        input: {
          phase: place * 0.9,
          index: place,
          hovered: hovered ? 0 : -1,
          selected: dragging,
          pointer: null,
          unit: this.size.height,
          wide: this.size.width,
        },
        wantHover: hovered ? 1 : 0,
        wantPress: dragging ? 1 : 0,
        // 拖着的那张脱离槽位、直接落在指针 x 上（y 仍走槽位），并压到最上面
        ...(dragging
          ? { override: [this.#dragX, slot.ty] as const, z: 9000 + place }
          : { z: place }),
      };
    });
    this.#rig.step(dt, 0, 0, specs);

    // ★ 连续 >4 张未知牌的数字：盖在那一张的**上方**（卡顶再往上）
    for (const slot of this.#slots) {
      if (slot.label === null) continue;
      const top = slot.view;
      slot.label.position.set(
        Math.round(top.position.x - slot.label.width / 2),
        Math.round(top.position.y - this.size.height / 2 - slot.label.height - 2),
      );
    }
  }

  update(ticker: { deltaMS: number }): void {
    this.step(ticker.deltaMS);
  }

  pulse(): void {
    this.#rig.pulse();
  }

  destroy(): void {
    this.#rig.clear();
    this.#slots = [];
    this.view.destroy({ children: true });
  }
}
